import type { ResultOf } from '@graphql-typed-document-node/core';

import Skeleton from '@mui/material/Skeleton';

import {
  getFragmentData,
  graphql,
  ResolvableModeEnum,
} from '@e-socna/data-layer';
import { isDefined } from '@e-socna/utils';
import { EMPTY_STRING } from '@e-socna/empty';
import type { FragmentType } from '@e-socna/data-layer';

import { formatPercent } from '../../utils/format-percent.util';
import { wrapExpression } from '../../utils/handlebars-expression.util';
import { N_A } from '../../constants/labels.constant';

import { useContentGenerator } from '../DocumentEditor/hooks/useContentGenerator.hook';

import { useResolvableNormalizer } from './hooks/useResolvableNormalizer';
import { PrimaryWrapper as PrimaryWrapperCmp } from './PrimaryWrapper';
import { SecondaryWrapper as SecondaryWrapperCmp } from './SecondaryWrapper';
import type { Props as BaseProps } from './types/props.type';

export const ResolvablePercentCmp_ResolvablePercent_Fragment = graphql(`
  fragment ResolvablePercentCmp_ResolvablePercent_Fragment on ResolvableFloat {
    __typename
    mode
    resolverId
    value
  }
`);

export type ResolvablePercentCmp_ResolvablePercent = ResultOf<
  typeof ResolvablePercentCmp_ResolvablePercent_Fragment
>;

interface Props extends BaseProps {
  readonly resolvablePercent: FragmentType<
    typeof ResolvablePercentCmp_ResolvablePercent_Fragment
  >;
}

export const ResolvablePercent = ({
  PrimaryWrapper = PrimaryWrapperCmp,
  resolvableFieldSettings,
  resolvablePercent: pResolvablePercent,
  SecondaryWrapper = SecondaryWrapperCmp,
}: Readonly<Props>) => {
  const resolvablePercent = getFragmentData(
    ResolvablePercentCmp_ResolvablePercent_Fragment,
    pResolvablePercent,
  );

  const normalizedPercent = useResolvableNormalizer(resolvablePercent);

  const { enableDirectGeneration, primaryMode } = resolvableFieldSettings;

  const { data: percentData, isSuccess: percentIsSuccess } =
    useContentGenerator({
      enabled:
        normalizedPercent.mode === ResolvableModeEnum.Dynamic &&
        enableDirectGeneration,
      template:
        normalizedPercent.mode === ResolvableModeEnum.Dynamic
          ? wrapExpression(normalizedPercent.resolverCode)
          : EMPTY_STRING,
    });

  if (normalizedPercent.mode === ResolvableModeEnum.Static) {
    if (!isDefined(normalizedPercent.value)) {
      return <PrimaryWrapper>{N_A}</PrimaryWrapper>;
    }

    return (
      <PrimaryWrapper>{formatPercent(normalizedPercent.value)}</PrimaryWrapper>
    );
  }

  const command = wrapExpression(normalizedPercent.merged);

  if (!enableDirectGeneration) {
    return <PrimaryWrapper>{command}</PrimaryWrapper>;
  }

  if (!percentIsSuccess) {
    return (
      <Skeleton sx={{ display: 'inline-block' }} variant="text" width={80} />
    );
  }

  const valueAsFloat = isDefined(percentData?.generated)
    ? Number.parseFloat(percentData.generated)
    : undefined;

  if (!isDefined(valueAsFloat)) {
    return <PrimaryWrapper>{N_A}</PrimaryWrapper>;
  }

  const formattedValue = formatPercent(valueAsFloat);

  return primaryMode === ResolvableModeEnum.Static ? (
    <>
      <PrimaryWrapper>{formattedValue}</PrimaryWrapper>{' '}
      <SecondaryWrapper>{command}</SecondaryWrapper>
    </>
  ) : (
    <>
      <PrimaryWrapper>{command}</PrimaryWrapper>{' '}
      <SecondaryWrapper>{formattedValue}</SecondaryWrapper>
    </>
  );
};
